/**
 * Collection / search filter drawer.
 *
 * Dual-mode: browser controller + CommonJS helpers for tests.
 * Selections apply on View, not on each tap. Facet values come from page JSON.
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  root.FabFilterDrawer = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function clonePending(pending) {
    var out = {};
    Object.keys(pending || {}).forEach(function (key) {
      out[key] = {};
      Object.keys(pending[key] || {}).forEach(function (value) {
        if (pending[key][value]) out[key][value] = true;
      });
    });
    return out;
  }

  function pendingFromFacets(facets) {
    var pending = {};
    (facets || []).forEach(function (facet) {
      pending[facet.param_name] = {};
      (facet.values || []).forEach(function (value) {
        if (value.active) pending[facet.param_name][value.value] = true;
      });
    });
    return pending;
  }

  function countSelected(pending, paramName) {
    var group = pending && pending[paramName];
    if (!group) return 0;
    return Object.keys(group).filter(function (key) {
      return group[key];
    }).length;
  }

  function totalSelected(pending) {
    return Object.keys(pending || {}).reduce(function (sum, key) {
      return sum + countSelected(pending, key);
    }, 0);
  }

  function setValue(pending, paramName, value, checked) {
    if (!pending[paramName]) pending[paramName] = {};
    if (checked) pending[paramName][value] = true;
    else delete pending[paramName][value];
  }

  function clearFacet(pending, paramName) {
    pending[paramName] = {};
  }

  function clearAll(pending) {
    Object.keys(pending || {}).forEach(function (key) {
      pending[key] = {};
    });
  }

  function buildSearchParams(pending, extras) {
    var params = new URLSearchParams();
    Object.keys(pending || {}).forEach(function (name) {
      Object.keys(pending[name] || {}).forEach(function (value) {
        if (pending[name][value]) params.append(name, value);
      });
    });
    if (extras) {
      Object.keys(extras).forEach(function (key) {
        if (extras[key] == null || extras[key] === '') return;
        params.set(key, extras[key]);
      });
    }
    return params.toString();
  }

  function pendingEquals(a, b) {
    return buildSearchParams(a) === buildSearchParams(b);
  }

  var COLOUR_SWATCHES = {
    black: '#1A1817',
    white: '#FFFFFF',
    cream: '#F3E6C8',
    ivory: '#F6EFE0',
    beige: '#E4C9A0',
    sand: '#E2C48A',
    'warm sand': '#E2C48A',
    neutral: '#C9C0B4',
    taupe: '#B39B84',
    grey: '#8E8E8E',
    gray: '#8E8E8E',
    'warm grey': '#A89B8C',
    'warm gray': '#A89B8C',
    'soft grey': '#C5C0B8',
    'soft gray': '#C5C0B8',
    charcoal: '#3C3C3C',
    brown: '#7A451C',
    terracotta: '#D85A2A',
    clay: '#C46A3A',
    'warm clay': '#C46A3A',
    rust: '#D3541A',
    caramel: '#C47A2A',
    bronze: '#CD7F32',
    copper: '#B87333',
    silver: '#C0C8D0',
    orange: '#FF6A00',
    peach: '#FFB07A',
    apricot: '#FFB070',
    coral: '#FF6F61',
    pink: '#FF4F8B',
    blush: '#FF8FAB',
    'blush pink': '#FF8FAB',
    rose: '#F25C7A',
    'rose pink': '#F25C7A',
    'dusty pink': '#E07A9A',
    'dusty rose': '#E08AA0',
    magenta: '#E1136E',
    berry: '#C41E6A',
    red: '#E03131',
    'brick red': '#C0392B',
    brick: '#C0392B',
    burgundy: '#8E1C3A',
    'deep burgundy': '#8E1C3A',
    maroon: '#8B1E3F',
    yellow: '#F5C400',
    gold: '#E0A800',
    mustard: '#E0B000',
    ochre: '#E09A1A',
    'golden ochre': '#E09A1A',
    amber: '#F0A202',
    honey: '#E8B84A',
    straw: '#E8D08A',
    'soft straw': '#E8D08A',
    green: '#2F9E44',
    sage: '#7CB069',
    'sage green': '#7CB069',
    olive: '#7A9B2E',
    'olive green': '#7A9B2E',
    moss: '#5C8A3A',
    'moss green': '#5C8A3A',
    lime: '#8FDC3A',
    'lime green': '#8FDC3A',
    leaf: '#4CAF50',
    'leafy green': '#4CAF50',
    forest: '#1F7A3A',
    'forest green': '#1F7A3A',
    pine: '#1B6B3A',
    'pine green': '#1B6B3A',
    jade: '#2FA36B',
    'jade green': '#2FA36B',
    eucalyptus: '#4AA882',
    mint: '#7DDBB0',
    teal: '#0F9B8E',
    'deep teal': '#0A7A70',
    turquoise: '#2EC4B6',
    aqua: '#3DCEC7',
    seafoam: '#6FCFC4',
    blue: '#2F6FED',
    navy: '#1E3A8A',
    'navy blue': '#1E3A8A',
    denim: '#3B6FA0',
    'denim blue': '#3B6FA0',
    cobalt: '#2952D6',
    'cobalt blue': '#2952D6',
    cornflower: '#6B91F0',
    'cornflower blue': '#6B91F0',
    'powder blue': '#A8C8F0',
    slate: '#5B7C9A',
    'slate blue': '#5B7C9A',
    indigo: '#4338CA',
    purple: '#7C3AED',
    violet: '#7C3AED',
    plum: '#8B3A8B',
    'deep plum': '#6B2A6B',
    lavender: '#B794F4',
    lilac: '#C084FC',
    'soft lilac': '#C084FC',
    mauve: '#C77DFF',
    'dusty mauve': '#C77DFF',
    rainbow:
      'conic-gradient(from 210deg, #E03131, #FF6A00, #F5C400, #2F9E44, #2F6FED, #7C3AED, #E03131)',
    multicolour:
      'conic-gradient(from 210deg, #E03131, #FF6A00, #F5C400, #2F9E44, #2F6FED, #7C3AED, #E03131)',
    multicolor:
      'conic-gradient(from 210deg, #E03131, #FF6A00, #F5C400, #2F9E44, #2F6FED, #7C3AED, #E03131)'
  };

  function canonicalValue(value) {
    if (!value) return '';
    if (value.value == null) return '';
    return String(value.value);
  }

  function displayOptionLabel(value) {
    if (!value) return '';
    if (value.label != null && String(value.label) !== '') return String(value.label);
    return canonicalValue(value);
  }

  function isFilterSettingGroupGid(value) {
    return /^gid:\/\/shopify\/FilterSettingGroup\//i.test(String(value || ''));
  }

  var visualIdentityByGid = {};

  // Search & Discovery stores translated filter options as FilterSettingGroup
  // GIDs, not English names. Shopify also redirects the unprefixed catalogue
  // to the visitor locale, so those GIDs are the stable visual identity.
  var FILTER_SETTING_IDENTITIES = {
    'gid://shopify/FilterSettingGroup/241008986': 'Beige',
    'gid://shopify/FilterSettingGroup/241041754': 'Black',
    'gid://shopify/FilterSettingGroup/241074522': 'Blue',
    'gid://shopify/FilterSettingGroup/241107290': 'Brown',
    'gid://shopify/FilterSettingGroup/312181082': 'Coral',
    'gid://shopify/FilterSettingGroup/241140058': 'Gold',
    'gid://shopify/FilterSettingGroup/241172826': 'Green',
    'gid://shopify/FilterSettingGroup/241205594': 'Grey',
    'gid://shopify/FilterSettingGroup/241271130': 'Orange',
    'gid://shopify/FilterSettingGroup/241303898': 'Peach',
    'gid://shopify/FilterSettingGroup/241336666': 'Pink',
    'gid://shopify/FilterSettingGroup/241369434': 'Purple',
    'gid://shopify/FilterSettingGroup/241434970': 'Red',
    'gid://shopify/FilterSettingGroup/241500506': 'White',
    'gid://shopify/FilterSettingGroup/241533274': 'Yellow',
    'gid://shopify/FilterSettingGroup/241238362': 'olive green',
    'gid://shopify/FilterSettingGroup/241402202': 'Rainbow',
    'gid://shopify/FilterSettingGroup/241467738': 'Silver',
    'gid://shopify/FilterSettingGroup/371425626': 'Monochrome',
    'gid://shopify/FilterSettingGroup/371294554': 'Soft Pastels',
    'gid://shopify/FilterSettingGroup/371130714': 'Cool Neutrals',
    'gid://shopify/FilterSettingGroup/371097946': 'Warm Neutrals',
    'gid://shopify/FilterSettingGroup/371163482': 'Earthy Tones',
    'gid://shopify/FilterSettingGroup/371196250': 'Heritage Tones',
    'gid://shopify/FilterSettingGroup/371261786': 'Mediterranean',
    'gid://shopify/FilterSettingGroup/371229018': 'Coastal',
    'gid://shopify/FilterSettingGroup/371327322': 'Bold Brights',
    'gid://shopify/FilterSettingGroup/371360090': 'Jewel Tones',
    'gid://shopify/FilterSettingGroup/371392858': 'Dark & Moody',
    'gid://shopify/FilterSettingGroup/302219610': 'Portrait',
    'gid://shopify/FilterSettingGroup/370999642': 'Living Room',
    'gid://shopify/FilterSettingGroup/370966874': 'Kitchen',
    'gid://shopify/FilterSettingGroup/370868570': 'Dining Room',
    'gid://shopify/FilterSettingGroup/370835802': 'Bedroom',
    'gid://shopify/FilterSettingGroup/370934106': "Kids' Room",
    'gid://shopify/FilterSettingGroup/371032410': 'Nursery',
    'gid://shopify/FilterSettingGroup/371589466': 'Bathroom',
    'gid://shopify/FilterSettingGroup/371065178': 'Office',
    'gid://shopify/FilterSettingGroup/371622234': 'Home Gym',
    'gid://shopify/FilterSettingGroup/370901338': 'Hallway'
  };

  function resetVisualIdentities() {
    visualIdentityByGid = {};
  }

  function indexVisualIdentities(payload) {
    ((payload && payload.facets) || []).forEach(function (facet) {
      (facet.values || []).forEach(function (value) {
        var id = canonicalValue(value);
        var name = displayOptionLabel(value);
        if (!isFilterSettingGroupGid(id) || !name || isFilterSettingGroupGid(name)) return;
        visualIdentityByGid[id] = name;
      });
    });
  }

  function visualIdentity(value) {
    if (!value) return '';
    if (value.identity != null && String(value.identity) !== '') return String(value.identity);
    var id = canonicalValue(value);
    if (id && !isFilterSettingGroupGid(id)) return id;
    if (id && visualIdentityByGid[id]) return visualIdentityByGid[id];
    if (id && FILTER_SETTING_IDENTITIES[id]) return FILTER_SETTING_IDENTITIES[id];
    return displayOptionLabel(value);
  }

  function lookupText(value) {
    return [visualIdentity(value), displayOptionLabel(value)].join(' ').toLowerCase();
  }

  function isSwatchFacet(facet) {
    if (!facet || isPaletteFacet(facet)) return false;
    if (facet.presentation === 'swatch') return true;
    var param = String(facet.param_name || '').toLowerCase();
    if (/colou?r/.test(param)) return true;
    var label = String(facet.label || '').toLowerCase();
    if (/colou?r|farbe|couleur|colore|kleur|färg|color/.test(label)) return true;
    return (facet.values || []).some(function (value) {
      return !!(value && value.swatch);
    });
  }

  var PALETTE_SWATCHES = {
    monochrome: 'conic-gradient(#1A1817 0deg 90deg, #6B6B67 90deg 180deg, #C5C0B8 180deg 270deg, #F5F0EB 270deg 360deg)',
    'soft pastels': 'conic-gradient(#F4C4D0 0deg 90deg, #C5B4E0 90deg 180deg, #B8E0D0 180deg 270deg, #F8D9B8 270deg 360deg)',
    'cool neutrals': 'conic-gradient(#8A9AAB 0deg 90deg, #C5D0D8 90deg 180deg, #A8B0B4 180deg 270deg, #E8EEF0 270deg 360deg)',
    'warm neutrals': 'conic-gradient(#E8D5C4 0deg 90deg, #C4A882 90deg 180deg, #B7AC9E 180deg 270deg, #F5F0EB 270deg 360deg)',
    'earthy tones': 'conic-gradient(#C4633E 0deg 90deg, #6E7444 90deg 180deg, #C68A3E 180deg 270deg, #B5714E 270deg 360deg)',
    'heritage tones': 'conic-gradient(#6E2E3A 0deg 90deg, #C8963C 90deg 180deg, #3F5E43 180deg 270deg, #EDE7DD 270deg 360deg)',
    mediterranean: 'conic-gradient(#C4633E 0deg 90deg, #2E8B8B 90deg 180deg, #E0A878 180deg 270deg, #F5F0EB 270deg 360deg)',
    coastal: 'conic-gradient(#6FB0B4 0deg 90deg, #E2C48A 90deg 180deg, #9DB3CC 180deg 270deg, #F5F0EB 270deg 360deg)',
    'bold brights': 'conic-gradient(#FF6A00 0deg 90deg, #2F6FED 90deg 180deg, #F5C400 180deg 270deg, #E1136E 270deg 360deg)',
    'jewel tones': 'conic-gradient(#0F743B 0deg 90deg, #2952D6 90deg 180deg, #8E1C3A 180deg 270deg, #6E5A9E 270deg 360deg)',
    'dark and moody': 'conic-gradient(#1A1817 0deg 90deg, #4E3450 90deg 180deg, #1F3A2A 180deg 270deg, #1E3A8A 270deg 360deg)'
  };

  function isPaletteFacet(facet) {
    if (!facet) return false;
    var label = String(facet.label || '').toLowerCase();
    var param = String(facet.param_name || '').toLowerCase();
    return /palette|tavolozza/.test(label) || param.indexOf('palette') !== -1;
  }

  function paletteKey(label) {
    return String(label == null ? '' : label)
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function resolvePaletteSwatch(value) {
    var key = paletteKey(visualIdentity(value));
    if (PALETTE_SWATCHES[key]) return PALETTE_SWATCHES[key];
    key = paletteKey(displayOptionLabel(value));
    return PALETTE_SWATCHES[key] || '';
  }

  function isRoomFacet(facet) {
    if (!facet) return false;
    var label = String(facet.label || '').toLowerCase();
    var param = String(facet.param_name || '').toLowerCase();
    if (/^rooms?$|raum|pièce|kamer|habitaci[oó]n|stanza/.test(label)) return true;
    return /(^|\.)room$/.test(param);
  }

  function roomKind(value) {
    var text = lookupText(value);
    if (/living|lounge|sitting|wohnzimmer|salon|soggiorno/.test(text)) return 'living';
    if (/bed|nursery|schlafzimmer|chambre|camera/.test(text)) return 'bedroom';
    if (/kitchen|küche|kuche|cuisine|cucina|cocina/.test(text)) return 'kitchen';
    if (/bath|bad|salle de bain|bagno|baño/.test(text)) return 'bathroom';
    if (/dining|esszimmer|salle à manger|pranzo|comedor/.test(text)) return 'dining';
    if (/kid|child|play|kinder|enfant|bambin/.test(text)) return 'kids';
    if (/office|study|desk|büro|buero|bureau|ufficio|despacho/.test(text)) return 'office';
    if (/hall|landing|stair|entry|foyer|flur|entrée|ingresso/.test(text)) return 'hallway';
    if (/garden|outdoor|patio|conservatory|garten|jardin|giardino/.test(text)) return 'garden';
    return 'room';
  }

  function isOrientationFacet(facet) {
    if (!facet) return false;
    var param = String(facet.param_name || '').toLowerCase();
    if (param.indexOf('orientation') !== -1) return true;
    if (/orient|ausrichtung|orientaci[oó]n|orientamento/.test(String(facet.label || '').toLowerCase())) {
      return true;
    }
    var values = facet.values || [];
    if (!values.length) return false;
    return values.every(function (value) {
      return orientationKind(value) !== '';
    });
  }

  function orientationKind(value) {
    var text = lookupText(value);
    if (/landscape|paysage|orizzontale|horizontal|querformat|liggend/.test(text)) return 'landscape';
    if (/portrait|hochformat|vertical|ritratto|staand|retrato/.test(text)) return 'portrait';
    if (/square|quadrat|carré|cuadrad|vierkant|cuadrado/.test(text)) return 'square';
    return '';
  }

  function hexFromLabel(label) {
    var raw = String(label == null ? '' : label).trim();
    var key = raw.toLowerCase();
    if (!key) return '';
    if (/^#?[0-9a-f]{3,8}$/i.test(key)) {
      return key.charAt(0) === '#' ? raw : '#' + raw;
    }
    if (COLOUR_SWATCHES[key]) return COLOUR_SWATCHES[key];
    var words = key.split(/[\s/_-]+/).filter(Boolean);
    for (var i = 0; i < words.length; i++) {
      if (COLOUR_SWATCHES[words[i]]) return COLOUR_SWATCHES[words[i]];
    }
    return '';
  }

  function resolveSwatch(value) {
    if (value && value.swatch) return value.swatch;
    var named = hexFromLabel(visualIdentity(value));
    if (named) return named;
    return hexFromLabel(displayOptionLabel(value));
  }

  function findFacet(facets, paramName) {
    for (var i = 0; i < (facets || []).length; i++) {
      if (facets[i].param_name === paramName) return facets[i];
    }
    return null;
  }

  function labelForValue(facets, paramName, canonical) {
    var facet = findFacet(facets, paramName);
    var values = (facet && facet.values) || [];
    var needle = String(canonical == null ? '' : canonical);
    for (var i = 0; i < values.length; i++) {
      if (canonicalValue(values[i]) === needle) {
        var label = displayOptionLabel(values[i]);
        if (label) return label;
      }
    }
    return toTitleCase(displayValueLabel(canonical));
  }

  function isFilterParam(key) {
    return String(key || '').indexOf('filter.') === 0;
  }

  function isPriceParam(key) {
    return key === 'filter.v.price.gte' || key === 'filter.v.price.lte';
  }

  function extrasFromLocation(search, sortValue) {
    var extras = {};
    var current = new URLSearchParams(search || '');
    current.forEach(function (value, key) {
      if (key === 'page') return;
      if (isFilterParam(key) && !isPriceParam(key)) return;
      extras[key] = value;
    });
    if (sortValue) extras.sort_by = sortValue;
    return extras;
  }

  function appliedFromSearch(search) {
    var pending = {};
    var params = new URLSearchParams(search || '');
    params.forEach(function (value, key) {
      if (!isFilterParam(key) || isPriceParam(key)) return;
      if (value == null || value === '') return;
      if (!pending[key]) pending[key] = {};
      pending[key][value] = true;
    });
    return pending;
  }

  function priceFromSearch(search) {
    var params = new URLSearchParams(search || '');
    return {
      min: params.get('filter.v.price.gte') || '',
      max: params.get('filter.v.price.lte') || ''
    };
  }

  function removeSearchParamValue(search, name, value) {
    var params = new URLSearchParams(search || '');
    var next = new URLSearchParams();
    params.forEach(function (currentValue, key) {
      if (key === 'page') return;
      if (key === name && String(currentValue) === String(value)) return;
      next.append(key, currentValue);
    });
    return next.toString();
  }

  function removePriceParams(search) {
    var params = new URLSearchParams(search || '');
    params.delete('filter.v.price.gte');
    params.delete('filter.v.price.lte');
    params.delete('page');
    return params.toString();
  }

  function displayValueLabel(value) {
    try {
      return decodeURIComponent(String(value == null ? '' : value).replace(/\+/g, ' '));
    } catch (e) {
      return String(value == null ? '' : value);
    }
  }

  function toTitleCase(value) {
    return String(value == null ? '' : value).replace(/\S+/g, function (word) {
      if (/^(?:[AB][0-9]|XS|S|M|L|XL|XXL|XXXL|[2-5]XL)$/i.test(word)) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
  }

  function normalizeQuery(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function textMatchesQuery(text, query) {
    if (!query) return true;
    return String(text == null ? '' : text)
      .toLowerCase()
      .indexOf(query) !== -1;
  }

  function optionMatchesQuery(value, query) {
    if (!query) return true;
    if (textMatchesQuery(displayOptionLabel(value), query)) return true;
    var identity = visualIdentity(value);
    if (identity && identity !== displayOptionLabel(value) && textMatchesQuery(identity, query)) {
      return true;
    }
    var id = canonicalValue(value);
    if (id && !isFilterSettingGroupGid(id) && textMatchesQuery(id, query)) return true;
    return false;
  }

  function facetMatchesQuery(facet, query) {
    if (!query) return true;
    if (textMatchesQuery(facet && facet.label, query)) return true;
    return (facet && facet.values ? facet.values : []).some(function (value) {
      return optionMatchesQuery(value, query);
    });
  }

  function collectMatchingOptions(facets, query) {
    var hits = [];
    (facets || []).forEach(function (facet) {
      var facetHit = textMatchesQuery(facet && facet.label, query);
      (facet && facet.values ? facet.values : []).forEach(function (value) {
        if (facetHit || optionMatchesQuery(value, query)) {
          hits.push({ facet: facet, value: value });
        }
      });
    });
    return hits;
  }

  var helpers = {
    clonePending: clonePending,
    pendingFromFacets: pendingFromFacets,
    countSelected: countSelected,
    totalSelected: totalSelected,
    setValue: setValue,
    clearFacet: clearFacet,
    clearAll: clearAll,
    buildSearchParams: buildSearchParams,
    pendingEquals: pendingEquals,
    isSwatchFacet: isSwatchFacet,
    isPaletteFacet: isPaletteFacet,
    resolvePaletteSwatch: resolvePaletteSwatch,
    isRoomFacet: isRoomFacet,
    roomKind: roomKind,
    isOrientationFacet: isOrientationFacet,
    orientationKind: orientationKind,
    hexFromLabel: hexFromLabel,
    resolveSwatch: resolveSwatch,
    canonicalValue: canonicalValue,
    displayOptionLabel: displayOptionLabel,
    visualIdentity: visualIdentity,
    indexVisualIdentities: indexVisualIdentities,
    resetVisualIdentities: resetVisualIdentities,
    isFilterSettingGroupGid: isFilterSettingGroupGid,
    labelForValue: labelForValue,
    findFacet: findFacet,
    extrasFromLocation: extrasFromLocation,
    appliedFromSearch: appliedFromSearch,
    priceFromSearch: priceFromSearch,
    removeSearchParamValue: removeSearchParamValue,
    removePriceParams: removePriceParams,
    displayValueLabel: displayValueLabel,
    isFilterParam: isFilterParam,
    isPriceParam: isPriceParam,
    toTitleCase: toTitleCase,
    normalizeQuery: normalizeQuery,
    textMatchesQuery: textMatchesQuery,
    optionMatchesQuery: optionMatchesQuery,
    facetMatchesQuery: facetMatchesQuery,
    collectMatchingOptions: collectMatchingOptions
  };

  if (typeof document === 'undefined') return helpers;

  var bound = false;
  var drawer = null;
  var applied = {};
  var pending = {};
  var mode = 'all';
  var currentParam = null;
  var applying = false;
  var pendingSort = null;
  var appliedSort = '';
  var countTimer = null;
  var countAbort = null;
  var countRequestId = 0;
  var closeIcon = '';
  var valuesByParam = null;
  var valuesFacets = null;
  var valuesKey = '';
  var valuesPromise = null;

  function t(key, fallback) {
    var parsed = getDataRaw() || {};
    var map = parsed.strings || {};
    var short = key.indexOf('.') >= 0 ? key.slice(key.indexOf('.') + 1) : key;
    var value = map[short];
    return typeof value === 'string' && value !== '' ? value : fallback;
  }

  function dataEl() {
    return document.querySelector('[data-fab-filter-data]');
  }

  function getDataRaw() {
    var el = dataEl();
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function liveValuesKey() {
    var params = new URLSearchParams(window.location.search || '');
    params.delete('page');
    params.delete('view');
    params.delete('section_id');
    params.delete('fab_facet');
    params.delete('sort_by');
    return window.location.pathname + '?' + params.toString();
  }

  function valuesAreFresh() {
    return !!(valuesByParam && valuesKey === liveValuesKey());
  }

  function getData() {
    var parsed = getDataRaw();
    if (!parsed) return null;
    if (valuesAreFresh() && valuesFacets) {
      parsed.facets = valuesFacets.map(function (facet) {
        var shell = findFacet(parsed.facets, facet.param_name);
        return {
          label: facet.label || (shell && shell.label) || facet.param_name,
          param_name: facet.param_name,
          presentation: facet.presentation || (shell && shell.presentation) || '',
          values_loaded: true,
          values: valuesByParam && valuesByParam[facet.param_name] ? valuesByParam[facet.param_name] : facet.values || []
        };
      });
    }
    return parsed;
  }

  function valuesRequestUrl(data) {
    var base = (data && data.values_url) || window.location.pathname + '?view=fab-filters';
    var params = new URLSearchParams(window.location.search || '');
    params.delete('page');
    params.delete('view');
    params.delete('section_id');
    params.delete('fab_facet');
    var qs = params.toString();
    if (!qs) return base;
    return base + (base.indexOf('?') === -1 ? '?' : '&') + qs;
  }

  function parseValuesHtml(html) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var node = doc.querySelector('[data-fab-filter-values]');
      if (!node) return null;
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function storeValues(payload) {
    valuesByParam = {};
    valuesFacets = payload && payload.facets ? payload.facets : [];
    valuesFacets.forEach(function (facet) {
      valuesByParam[facet.param_name] = facet.values || [];
    });
    valuesKey = liveValuesKey();
  }

  function needsValueFetch(data) {
    if (!data || !data.values_url) return false;
    if (valuesAreFresh()) return false;
    return (data.facets || []).some(function (facet) {
      return facet.values_loaded === false;
    });
  }

  function ensureValues() {
    var data = getDataRaw();
    if (!needsValueFetch(data)) return Promise.resolve(getData());
    if (valuesPromise) return valuesPromise;
    var requestKey = liveValuesKey();
    valuesPromise = fetch(valuesRequestUrl(data), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('filter values');
        return response.text();
      })
      .then(function (html) {
        if (requestKey !== liveValuesKey()) {
          valuesPromise = null;
          return getData();
        }
        var parsed = parseValuesHtml(html);
        if (parsed) storeValues(parsed);
        valuesPromise = null;
        syncUnavailableChips();
        renderActiveChips(appliedFromSearch(window.location.search));
        return getData();
      })
      .catch(function () {
        valuesPromise = null;
        return getData();
      });
    return valuesPromise;
  }

  function liveSortValue() {
    var select = document.getElementById('SortBy');
    return select ? select.value : '';
  }

  function sortValue() {
    return pendingSort != null ? pendingSort : liveSortValue();
  }

  function commitSort() {
    var select = document.getElementById('SortBy');
    if (select && pendingSort != null) select.value = pendingSort;
  }

  function sortOptionName(data, value) {
    var opts = (data && data.sort_options) || [];
    for (var i = 0; i < opts.length; i++) {
      if (opts[i].value === value) return opts[i].name;
    }
    return '';
  }

  function iconCloseHtml() {
    if (closeIcon) return closeIcon;
    var existing = document.querySelector('.drawer__close .svg-wrapper');
    if (existing) {
      closeIcon = existing.outerHTML;
      return closeIcon;
    }
    return '<span class="svg-wrapper" aria-hidden="true">×</span>';
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function viewSpinnerHtml() {
    return (
      '<span class="fab-filter-drawer__spinner hidden" data-fab-filter-view-spinner aria-hidden="true">' +
      '<svg viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" focusable="false">' +
      '<circle class="fab-filter-drawer__spinner-path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
      '</svg>' +
      '</span>'
    );
  }

  function searchIconHtml() {
    return (
      '<span class="fab-filter-drawer__search-icon" aria-hidden="true">' +
      '<svg viewBox="0 0 16 16" width="16" height="16" focusable="false">' +
      '<path fill="currentColor" d="M11.74 10.68a6 6 0 1 0-1.06 1.06l3.3 3.3a.75.75 0 0 0 1.06-1.06l-3.3-3.3zM7 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10z"/>' +
      '</svg>' +
      '</span>'
    );
  }

  function ensureDrawer() {
    if (drawer && document.body.contains(drawer) && drawer.querySelector('[data-fab-filter-query]')) {
      return drawer;
    }
    var existing = document.getElementById('FacetDrawer');
    if (existing && existing.querySelector('[data-fab-filter-query]')) {
      drawer = existing;
      return drawer;
    }
    if (existing) existing.remove();

    drawer = document.createElement('fab-drawer');
    drawer.id = 'FacetDrawer';
    drawer.className = 'drawer drawer--has-footer fab-filter-drawer';
    drawer.innerHTML =
      '<div class="cart-drawer__overlay" data-drawer-overlay></div>' +
      '<div class="drawer__inner" role="dialog" aria-modal="true" aria-labelledby="FacetDrawerHeading" tabindex="-1">' +
      '<div class="drawer__header fab-filter-drawer__header">' +
      '<button type="button" class="fab-filter-drawer__back" data-fab-filter-back hidden>' +
      '<span class="visually-hidden">' +
      escapeHtml(t('filters.back', 'Back')) +
      '</span>' +
      '<svg width="14" height="10" viewBox="0 0 14 10" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M5.463.808a.5.5 0 0 0-.817-.162l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 1 0 .708-.708L2.207 5.5H13a.5.5 0 0 0 0-1H2.207L5.354 1.354A.5.5 0 0 0 5.463.808"/></svg>' +
      '</button>' +
      '<h2 class="drawer__heading" id="FacetDrawerHeading" data-drawer-initial-focus tabindex="-1"></h2>' +
      '<button type="button" class="drawer__close" data-drawer-close aria-label="' +
      escapeHtml(t('filters.close', 'Close')) +
      '">' +
      iconCloseHtml() +
      '</button>' +
      '</div>' +
      '<div class="fab-filter-drawer__search">' +
      searchIconHtml() +
      '<label class="visually-hidden" for="FacetDrawerSearch">' +
      escapeHtml(t('filters.search', 'Search filters')) +
      '</label>' +
      '<input id="FacetDrawerSearch" class="fab-filter-drawer__search-input" type="search" data-fab-filter-query autocomplete="off" autocorrect="off" spellcheck="false" enterkeyhint="search" placeholder="' +
      escapeHtml(t('filters.search', 'Search filters')) +
      '">' +
      '<button type="button" class="fab-filter-drawer__search-clear" data-fab-filter-query-clear hidden aria-label="' +
      escapeHtml(t('filters.search_clear', 'Clear search')) +
      '">' +
      '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M1.2 1.2a.75.75 0 0 1 1.06 0L6 4.94 9.74 1.2A.75.75 0 1 1 10.8 2.26L7.06 6l3.74 3.74a.75.75 0 1 1-1.06 1.06L6 7.06 2.26 10.8A.75.75 0 0 1 1.2 9.74L4.94 6 1.2 2.26a.75.75 0 0 1 0-1.06z"/></svg>' +
      '</button>' +
      '</div>' +
      '<div class="fab-filter-drawer__viewport">' +
      '<div class="fab-filter-drawer__panels" data-fab-filter-panels>' +
      '<div class="fab-filter-drawer__level" data-fab-filter-index role="list"></div>' +
      '<div class="fab-filter-drawer__level" data-fab-filter-options></div>' +
      '</div>' +
      '</div>' +
      '<div class="drawer__footer-fixed fab-filter-drawer__footer">' +
      '<button type="button" class="fab-filter-drawer__clear" data-fab-filter-clear></button>' +
      '<button type="button" class="button fab-filter-drawer__view" data-fab-filter-view>' +
      '<span class="fab-filter-drawer__view-label" data-fab-filter-view-label></span>' +
      viewSpinnerHtml() +
      '</button>' +
      '</div>' +
      '</div>';

    document.body.appendChild(drawer);

    var protoClose = drawer.close.bind(drawer);
    drawer.close = function () {
      if (!applying) {
        pending = clonePending(applied);
        pendingSort = appliedSort;
      }
      protoClose();
      setExpanded(null);
      abortCount();
      setViewLoading(false);
    };

    drawer.querySelector('[data-fab-filter-back]').addEventListener('click', function () {
      showIndex();
    });
    drawer.querySelector('[data-fab-filter-clear]').addEventListener('click', onClear);
    drawer.querySelector('[data-fab-filter-view]').addEventListener('click', onView);
    drawer.querySelector('[data-fab-filter-options]').addEventListener('change', onOptionChange);
    drawer.querySelector('[data-fab-filter-index]').addEventListener('change', onOptionChange);

    var searchInput = drawer.querySelector('[data-fab-filter-query]');
    var searchClear = drawer.querySelector('[data-fab-filter-query-clear]');
    searchInput.addEventListener('input', onQueryInput);
    searchInput.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') event.preventDefault();
      if (event.key === 'Escape' && searchInput.value) {
        event.preventDefault();
        event.stopPropagation();
        resetQuery();
      }
    });
    searchInput.addEventListener('keyup', function (event) {
      if (event.key === 'Escape') event.stopPropagation();
    });
    searchClear.addEventListener('click', function () {
      resetQuery();
      searchInput.focus();
    });

    return drawer;
  }

  function queryInput() {
    return drawer && drawer.querySelector('[data-fab-filter-query]');
  }

  function currentQuery() {
    var input = queryInput();
    return normalizeQuery(input && input.value);
  }

  function updateSearchClear() {
    var input = queryInput();
    var clearBtn = drawer && drawer.querySelector('[data-fab-filter-query-clear]');
    if (!clearBtn || !input) return;
    clearBtn.hidden = !input.value;
  }

  function resetQuery() {
    var input = queryInput();
    if (input) input.value = '';
    updateSearchClear();
    onQueryInput();
  }

  function onQueryInput() {
    updateSearchClear();
    if (!drawer) return;
    var paint = function () {
      if (mode === 'all') renderIndex();
      else if (mode === 'sort') renderSortOptions();
      else if (currentParam) {
        var data = getData();
        var facet = findFacet(data && data.facets, currentParam);
        if (facet) renderOptions(facet);
      }
    };
    if (currentQuery() && needsValueFetch(getDataRaw())) {
      ensureValues().then(paint);
      return;
    }
    paint();
  }

  function emptySearchHtml() {
    return (
      '<p class="fab-filter-drawer__empty" role="status">' +
      escapeHtml(t('filters.search_empty', 'No matching filters')) +
      '</p>'
    );
  }

  function renderLoading(root) {
    if (!root) return;
    root.innerHTML =
      '<div class="fab-filter-drawer__loading" role="status">' +
      '<span class="visually-hidden">' +
      escapeHtml(t('filters.loading', 'Loading filters')) +
      '</span>' +
      '<span class="fab-filter-drawer__skeleton" aria-hidden="true"></span>' +
      '<span class="fab-filter-drawer__skeleton" aria-hidden="true"></span>' +
      '<span class="fab-filter-drawer__skeleton" aria-hidden="true"></span>' +
      '<span class="fab-filter-drawer__skeleton" aria-hidden="true"></span>' +
      '<span class="fab-filter-drawer__skeleton" aria-hidden="true"></span>' +
      '<span class="fab-filter-drawer__skeleton" aria-hidden="true"></span>' +
      '</div>';
  }

  function setExpanded(trigger) {
    document.querySelectorAll('[data-fab-filter-open]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', trigger && btn === trigger ? 'true' : 'false');
    });
  }

  function headingEl() {
    return drawer.querySelector('#FacetDrawerHeading');
  }

  function panelsEl() {
    return drawer.querySelector('[data-fab-filter-panels]');
  }

  function showIndex() {
    mode = 'all';
    currentParam = null;
    headingEl().textContent = t('filters.all', 'Filters');
    drawer.classList.remove('is-sort');
    drawer.querySelector('[data-fab-filter-back]').hidden = true;
    panelsEl().classList.remove('is-level-1');
    renderIndex();
    updateFooter();
    headingEl().focus();
  }

  function showSort() {
    var data = getData();
    if (!data || !(data.sort_options || []).length) return;
    mode = 'sort';
    currentParam = null;
    headingEl().textContent = t('filters.sort', 'Sort');
    drawer.classList.add('is-sort');
    var back = drawer.querySelector('[data-fab-filter-back]');
    back.hidden = false;
    renderSortOptions();
    panelsEl().classList.add('is-level-1');
    updateFooter();
    back.focus();
  }

  function showFacet(paramName) {
    var data = getData();
    var facet = findFacet(data && data.facets, paramName);
    if (!facet) return;
    mode = 'nested';
    currentParam = paramName;
    headingEl().textContent = toTitleCase(facet.label);
    drawer.classList.remove('is-sort');
    var back = drawer.querySelector('[data-fab-filter-back]');
    back.hidden = false;
    if (needsValueFetch(getDataRaw())) {
      renderLoading(drawer.querySelector('[data-fab-filter-options]'));
      panelsEl().classList.add('is-level-1');
      updateFooter();
      ensureValues().then(function () {
        if (mode !== 'nested' || currentParam !== paramName) return;
        var loaded = getData();
        var next = findFacet(loaded && loaded.facets, paramName);
        if (next) renderOptions(next);
        else showIndex();
        updateFooter();
      });
      back.focus();
      return;
    }
    renderOptions(facet);
    panelsEl().classList.add('is-level-1');
    updateFooter();
    back.focus();
  }

  function renderIndex() {
    var data = getData();
    var root = drawer.querySelector('[data-fab-filter-index]');
    if (!data || !root) return;
    var query = currentQuery();
    if (query) {
      renderSearchHits(root, data.facets, query);
      return;
    }
    var sortHtml = sortIndexRowHtml(data);
    root.innerHTML =
      sortHtml +
      (data.facets || [])
        .map(function (facet) {
        var count = countSelected(pending, facet.param_name);
        var countHtml =
          '<span class="fab-filter-drawer__index-count"' +
          (count ? '' : ' hidden') +
          '>' +
          (count ? '(' + count + ')' : '') +
          '</span>';
        return (
          '<button type="button" class="fab-filter-drawer__index-row" role="listitem" data-fab-filter-index-row data-param="' +
          escapeHtml(facet.param_name) +
          '">' +
          '<span class="fab-filter-drawer__index-label">' +
          escapeHtml(toTitleCase(facet.label)) +
          ' ' +
          countHtml +
          '</span>' +
          '<svg class="fab-filter-drawer__chevron" width="14" height="10" viewBox="0 0 14 10" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546"/></svg>' +
          '</button>'
        );
      })
      .join('');

    var sortRow = root.querySelector('[data-fab-filter-sort-row]');
    if (sortRow) {
      sortRow.addEventListener('click', showSort);
    }
    root.querySelectorAll('[data-fab-filter-index-row]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        showFacet(btn.getAttribute('data-param'));
      });
    });
  }

  function sortIndexRowHtml(data) {
    if (!document.getElementById('SortBy') || !(data.sort_options || []).length) return '';
    var label = sortOptionName(data, sortValue());
    return (
      '<button type="button" class="fab-filter-drawer__index-row" data-fab-filter-sort-row>' +
      '<span class="fab-filter-drawer__index-label">' +
      escapeHtml(t('filters.sort', 'Sort')) +
      '</span>' +
      '<span class="fab-filter-drawer__index-aside">' +
      '<span class="fab-filter-drawer__index-meta">' +
      escapeHtml(label) +
      '</span>' +
      '<svg class="fab-filter-drawer__chevron" width="14" height="10" viewBox="0 0 14 10" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546"/></svg>' +
      '</span>' +
      '</button>'
    );
  }

  function renderSortOptions() {
    var data = getData();
    var root = drawer.querySelector('[data-fab-filter-options]');
    if (!data || !root) return;
    var current = sortValue();
    root.innerHTML =
      '<fieldset class="fab-filter-drawer__fieldset">' +
      '<legend class="visually-hidden">' +
      escapeHtml(t('filters.sort', 'Sort')) +
      '</legend>' +
      (data.sort_options || [])
        .map(function (option, index) {
          var id = 'FacetDrawer-sort-' + index;
          var checked = option.value === current ? ' checked' : '';
          return (
            '<label class="fab-filter-drawer__option" for="' +
            escapeHtml(id) +
            '">' +
            '<input class="visually-hidden" type="radio" id="' +
            escapeHtml(id) +
            '" name="fab-filter-sort" value="' +
            escapeHtml(option.value) +
            '"' +
            checked +
            '>' +
            '<span class="fab-filter-drawer__check" aria-hidden="true"></span>' +
            '<span class="fab-filter-drawer__option-label">' +
            escapeHtml(option.name) +
            '</span>' +
            '</label>'
          );
        })
        .join('') +
      '</fieldset>';
  }

  function renderSearchHits(root, facets, query) {
    var hits = collectMatchingOptions(facets, query);
    if (!hits.length) {
      root.innerHTML = emptySearchHtml();
      return;
    }
    root.innerHTML =
      '<fieldset class="fab-filter-drawer__fieldset">' +
      '<legend class="visually-hidden">' +
      escapeHtml(t('filters.search', 'Search filters')) +
      '</legend>' +
      hits
        .map(function (hit, index) {
          return optionHtml(hit.facet, hit.value, index, 'FacetDrawer-search', true);
        })
        .join('') +
      '</fieldset>';
  }

  function optionHtml(facet, value, index, idPrefix, showFacetName) {
    var id = idPrefix + '-' + String(facet.param_name).replace(/[^a-z0-9]+/gi, '-') + '-' + index;
    var selected = pending[facet.param_name] || {};
    var checked = selected[value.value] ? ' checked' : '';
    var colour = isSwatchFacet(facet);
    var palette = isPaletteFacet(facet);
    var disc = colour || palette;
    var orient = isOrientationFacet(facet) && !disc;
    var room = isRoomFacet(facet) && !disc && !orient;
    var kind = orient ? orientationKind(value) : room ? roomKind(value) : '';
    var optionClass = 'fab-filter-drawer__option';
    if (disc) optionClass += ' fab-filter-drawer__option--swatch';
    if (orient && !showFacetName) optionClass += ' fab-filter-drawer__option--icon';
    if (room && !showFacetName) optionClass += ' fab-filter-drawer__option--pill';
    var controlHtml = disc || orient || room ? visualMarkHtml(disc, palette, kind, value, room) : checkMarkHtml();
    var facetHtml = showFacetName
      ? '<span class="fab-filter-drawer__option-facet">' + escapeHtml(toTitleCase(facet.label)) + '</span>'
      : '';
    return (
      '<label class="' +
      optionClass +
      '" for="' +
      escapeHtml(id) +
      '">' +
      '<input class="visually-hidden" type="checkbox" id="' +
      escapeHtml(id) +
      '" name="' +
      escapeHtml(value.param_name || facet.param_name) +
      '" value="' +
      escapeHtml(value.value) +
      '"' +
      checked +
      '>' +
      controlHtml +
      '<span class="fab-filter-drawer__option-label">' +
      escapeHtml(toTitleCase(displayOptionLabel(value))) +
      '</span>' +
      facetHtml +
      '</label>'
    );
  }

  function checkMarkHtml() {
    return '<span class="fab-filter-drawer__check" aria-hidden="true"></span>';
  }

  function visualMarkHtml(disc, palette, kind, value, room) {
    if (disc) {
      var fill = palette ? resolvePaletteSwatch(value) : resolveSwatch(value);
      return (
        '<span class="fab-filter-drawer__swatch" style="' +
        swatchStyle(fill) +
        '" aria-hidden="true"></span>'
      );
    }
    if (room) {
      return (
        '<span class="fab-filter-drawer__room-icon" aria-hidden="true">' +
        roomIconSvg(kind) +
        '</span>'
      );
    }
    return (
      '<span class="fab-filter-drawer__orient fab-filter-drawer__orient--' +
      escapeHtml(kind || 'unknown') +
      '" aria-hidden="true">' +
      orientationIconSvg(kind) +
      '</span>'
    );
  }

  function roomIconSvg(kind) {
    var inner = {
      living:
        '<path d="M5 11V9.2C5 7.9 6 7 7.3 7h9.4C18 7 19 7.9 19 9.2V11"/><path d="M4.2 10.8c-1 0-1.7.8-1.7 1.8v4.3h19v-4.3c0-1-.7-1.8-1.7-1.8s-1.8.8-1.8 1.8v1H6v-1c0-1-.8-1.8-1.8-1.8Z"/><path d="M5.2 16.9v2M18.8 16.9v2"/>',
      bedroom:
        '<path d="M3.5 18.5V8"/><path d="M3.5 13h17c.8 0 1.5.7 1.5 1.5v4H3.5V13Z"/><path d="M6 9.2c0-.7.5-1.2 1.2-1.2h4.3c.7 0 1.2.5 1.2 1.2V13H6V9.2ZM3.5 18.5v2M22 18.5v2"/>',
      kitchen:
        '<path d="M5 10h12v5.2c0 2.6-2.1 4.8-4.8 4.8H9.8C7.1 20 5 17.8 5 15.2V10Z"/><path d="M17 12h3.5"/><path d="M7 7.5h8M9 5h4"/>',
      bathroom:
        '<path d="M3.5 11.5h17l-1.1 4.4c-.5 2-2.3 3.6-4.4 3.6H9c-2.1 0-3.9-1.6-4.4-3.6l-1.1-4.4Z"/><path d="M7 11.5V7.8c0-1.9 1.4-3.3 3.2-3.3 1.3 0 2.3.7 2.9 1.7"/><path d="M12.3 7.2h3"/>',
      dining:
        '<rect x="6" y="9" width="12" height="4" rx="1"/><path d="M8 13v6M16 13v6"/><path d="M3.5 11v6h3M20.5 11v6h-3"/>',
      kids:
        '<circle cx="7.4" cy="7.6" r="2.1"/><circle cx="16.6" cy="7.6" r="2.1"/><path d="M7.2 10.3a4.8 4.8 0 0 1 9.6 0v1.5a4.8 4.8 0 0 1-9.6 0v-1.5Z"/><path d="M8.7 15.2C6.7 16.3 5.7 18 5.8 20h12.4c.1-2-1-3.7-2.9-4.8"/>',
      office:
        '<rect x="4" y="5" width="16" height="10.5" rx="1.5"/><path d="M12 15.5V19M8.5 19h7"/>',
      hallway:
        '<path d="M8 20V9.5L12 6l4 3.5V20"/><path d="M10.5 20v-5h3v5"/>',
      garden:
        '<path d="M12 20V6"/><path d="M12 10C7.6 10 5.4 7.9 5.5 4.5c3.8-.1 6 1.8 6.5 5.5Z"/><path d="M12 15c4.4 0 6.6-2.1 6.5-5.5-3.8-.1-6 1.8-6.5 5.5Z"/>',
      room:
        '<path d="M3.5 11.2 12 4l8.5 7.2v8.3H3.5v-8.3Z"/><path d="M9.5 19.5v-5h5v5"/>'
    };
    return (
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" focusable="false">' +
      (inner[kind] || inner.room) +
      '</svg>'
    );
  }

  function orientationIconSvg(kind) {
    if (kind === 'landscape') {
      return '<svg viewBox="0 0 28 20" width="28" height="20" focusable="false"><rect x="1.25" y="3.25" width="25.5" height="13.5" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
    }
    if (kind === 'portrait') {
      return '<svg viewBox="0 0 20 28" width="20" height="28" focusable="false"><rect x="3.25" y="1.25" width="13.5" height="25.5" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
    }
    if (kind === 'square') {
      return '<svg viewBox="0 0 24 24" width="24" height="24" focusable="false"><rect x="3.25" y="3.25" width="17.5" height="17.5" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
    }
    return '<svg viewBox="0 0 24 24" width="24" height="24" focusable="false"><rect x="4.25" y="5.25" width="15.5" height="13.5" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
  }

  function swatchStyle(swatch) {
    if (!swatch) return '';
    if (swatch.indexOf('url(') === 0) {
      return 'background-image:' + swatch + ';background-size:cover;background-position:center;';
    }
    if (swatch.indexOf('gradient') !== -1) {
      return 'background-image:' + swatch + ';';
    }
    return 'background-color:' + swatch + ';';
  }

  function renderOptions(facet) {
    var root = drawer.querySelector('[data-fab-filter-options]');
    var query = currentQuery();
    var values = (facet.values || []).filter(function (value) {
      return optionMatchesQuery(value, query);
    });
    if (!values.length) {
      root.innerHTML = emptySearchHtml();
      return;
    }
    var fieldsetClass = 'fab-filter-drawer__fieldset';
    if (isOrientationFacet(facet) && !isSwatchFacet(facet) && !isPaletteFacet(facet)) {
      fieldsetClass += ' fab-filter-drawer__fieldset--icon';
    }
    if (isRoomFacet(facet) && !isSwatchFacet(facet) && !isPaletteFacet(facet)) {
      fieldsetClass += ' fab-filter-drawer__fieldset--pills';
    }
    root.innerHTML =
      '<fieldset class="' +
      fieldsetClass +
      '">' +
      '<legend class="visually-hidden">' +
      escapeHtml(facet.label) +
      '</legend>' +
      values
        .map(function (value, index) {
          return optionHtml(facet, value, index, 'FacetDrawer', false);
        })
        .join('') +
      '</fieldset>';
  }

  function onOptionChange(event) {
    var input = event.target;
    if (!input) return;
    if (input.type === 'radio' && input.name === 'fab-filter-sort') {
      pendingSort = input.value;
      updateFooter();
      scheduleCount();
      return;
    }
    if (input.type !== 'checkbox') return;
    setValue(pending, input.name, input.value, input.checked);
    updateFooter();
    scheduleCount();
  }

  function onClear() {
    if (mode === 'all') {
      clearAll(pending);
      renderIndex();
      drawer.querySelectorAll('[data-fab-filter-options] input[type="checkbox"]').forEach(function (input) {
        input.checked = false;
      });
    } else if (mode === 'sort') {
      pendingSort = appliedSort;
      renderSortOptions();
    } else if (currentParam) {
      clearFacet(pending, currentParam);
      var data = getData();
      var facet = findFacet(data && data.facets, currentParam);
      if (facet) renderOptions(facet);
    }
    updateFooter();
    scheduleCount();
  }

  function viewLabel(count) {
    if (typeof count === 'number' && !isNaN(count)) {
      var prints =
        count === 1
          ? t('toast.prints_one', '1 print').replace('1', '1')
          : t('toast.prints_other', '{n} prints').replace('{n}', String(count));
      return t('filters.view', 'View') + ' ' + prints;
    }
    return t('filters.view_results', 'View results');
  }

  function updateFooter() {
    var clearBtn = drawer.querySelector('[data-fab-filter-clear]');
    var viewLabelNode = drawer.querySelector('[data-fab-filter-view-label]');
    var data = getData();
    if (mode === 'sort') {
      clearBtn.hidden = true;
    } else {
      clearBtn.hidden = false;
    }
    if (mode === 'all') {
      clearBtn.textContent = t('filters.clear_all', 'Clear all');
    } else if (mode !== 'sort') {
      var facet = findFacet(data && data.facets, currentParam);
      var name = facet ? toTitleCase(facet.label) : '';
      clearBtn.textContent = t('filters.clear_facet', 'Clear {name}').replace('{name}', name);
    }
    var cheapCount =
      pendingEquals(pending, applied) && pendingSort === appliedSort ? data && data.results_count : null;
    if (viewLabelNode) {
      viewLabelNode.textContent = viewLabel(typeof cheapCount === 'number' ? cheapCount : undefined);
    }
  }

  function setViewLoading(isLoading) {
    if (!drawer) return;
    var spinner = drawer.querySelector('[data-fab-filter-view-spinner]');
    var viewBtn = drawer.querySelector('[data-fab-filter-view]');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
    if (viewBtn) {
      viewBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      viewBtn.disabled = false;
    }
  }

  function abortCount() {
    countRequestId += 1;
    if (countTimer) {
      clearTimeout(countTimer);
      countTimer = null;
    }
    if (countAbort) {
      countAbort.abort();
      countAbort = null;
    }
  }

  function scheduleCount() {
    abortCount();
    if (pendingEquals(pending, applied)) {
      updateFooter();
      setViewLoading(false);
      return;
    }
    setViewLoading(true);
    var requestId = countRequestId;
    countTimer = setTimeout(function () {
      if (requestId !== countRequestId) return;
      fetchPendingCount(requestId);
    }, 360);
  }

  function parseCountFromHtml(html) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var node = doc.querySelector('[data-fab-filter-data]');
      if (node) {
        var parsed = JSON.parse(node.textContent);
        if (typeof parsed.results_count === 'number') return parsed.results_count;
      }
      var attr = doc.querySelector('[data-results-count]');
      if (attr && attr.getAttribute('data-results-count')) {
        return parseInt(attr.getAttribute('data-results-count'), 10);
      }
    } catch (e) {}
    return null;
  }

  function fetchPendingCount(requestId) {
    var grid = document.getElementById('ProductGridContainer');
    var sectionId = grid && (grid.dataset.sectionId || (grid.closest('[data-section-id]') || {}).dataset.sectionId);
    if (!sectionId || typeof fetch === 'undefined') {
      if (requestId === countRequestId) setViewLoading(false);
      return;
    }
    var params = buildSearchParams(pending, extrasFromLocation(window.location.search, sortValue()));
    var url = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId) + '&' + params;
    countAbort = new AbortController();
    fetch(url, { signal: countAbort.signal })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        if (requestId !== countRequestId) return;
        var count = parseCountFromHtml(html);
        var viewLabelNode = drawer && drawer.querySelector('[data-fab-filter-view-label]');
        if (viewLabelNode && count != null && !isNaN(count)) {
          viewLabelNode.textContent = viewLabel(count);
        }
        setViewLoading(false);
      })
      .catch(function () {
        if (requestId !== countRequestId) return;
        setViewLoading(false);
      });
  }

  function facetLabelForParam(paramName) {
    var data = getData();
    var facet = findFacet(data && data.facets, paramName);
    if (facet && facet.label) return toTitleCase(facet.label);
    var raw = dataEl();
    if (!raw) return paramName;
    try {
      var parsed = JSON.parse(raw.textContent);
      facet = findFacet(parsed.facets, paramName);
      return facet && facet.label ? toTitleCase(facet.label) : paramName;
    } catch (e) {
      return paramName;
    }
  }

  function syncUnavailableChips() {
    var buttons = document.querySelectorAll('.fab-filter-btn[data-param]');
    if (!valuesAreFresh() || !valuesFacets) {
      buttons.forEach(function (btn) {
        btn.hidden = false;
      });
      return;
    }
    var available = {};
    valuesFacets.forEach(function (facet) {
      available[facet.param_name] = true;
    });
    buttons.forEach(function (btn) {
      var param = btn.getAttribute('data-param');
      btn.hidden = !available[param];
    });
  }

  function seedHiddenInputs(pendingState) {
    var root = document.querySelector('[data-fab-filter-hidden]');
    if (!root) return;
    var html = '';
    Object.keys(pendingState || {}).forEach(function (name) {
      Object.keys(pendingState[name] || {}).forEach(function (value) {
        if (!pendingState[name][value]) return;
        html +=
          '<input type="hidden" name="' +
          escapeHtml(name) +
          '" value="' +
          escapeHtml(value) +
          '">';
      });
    });
    var price = priceFromSearch(window.location.search);
    if (price.min) {
      html +=
        '<input type="hidden" name="filter.v.price.gte" value="' + escapeHtml(price.min) + '">';
    }
    if (price.max) {
      html +=
        '<input type="hidden" name="filter.v.price.lte" value="' + escapeHtml(price.max) + '">';
    }
    root.innerHTML = html;
  }

  function updateChipCounts(pendingState) {
    document.querySelectorAll('.fab-filter-btn[data-param]').forEach(function (btn) {
      var param = btn.getAttribute('data-param');
      var count = countSelected(pendingState, param);
      btn.classList.toggle('is-active', count > 0);
      var countEl = btn.querySelector('.fab-filter-btn__count');
      if (count > 0) {
        if (!countEl) {
          countEl = document.createElement('span');
          countEl.className = 'fab-filter-btn__count';
          btn.appendChild(countEl);
        }
        countEl.textContent = '(' + count + ')';
      } else if (countEl) {
        countEl.remove();
      }
    });
    var floatCount = document.querySelector('[data-fab-filter-float-count]');
    var total = totalSelected(pendingState);
    var price = priceFromSearch(window.location.search);
    if (price.min || price.max) total += 1;
    if (floatCount) {
      floatCount.hidden = total < 1;
      floatCount.textContent = total > 0 ? String(total) : '';
    }
  }

  function renderActiveChips(pendingState) {
    var list = document.querySelector('[data-fab-active-chips]');
    var wrap = document.querySelector('[data-fab-active-facets]');
    if (!list || !wrap) return;
    var chips = [];
    Object.keys(pendingState || {}).forEach(function (name) {
      Object.keys(pendingState[name] || {}).forEach(function (value) {
        if (!pendingState[name][value]) return;
        var qs = removeSearchParamValue(window.location.search, name, value);
        var href = window.location.pathname + (qs ? '?' + qs : '');
        chips.push(
          '<facet-remove>' +
            '<a href="' +
            escapeHtml(href) +
            '" class="active-facets__button active-facets__button--light js-facet-remove" data-filter-name="' +
            escapeHtml(name) +
            '" data-filter-value="' +
            escapeHtml(value) +
            '">' +
            '<span class="active-facets__button-inner button button--tertiary">' +
            escapeHtml(labelForValue((getData() && getData().facets) || [], name, value)) +
            ' ×' +
            '<span class="visually-hidden">' +
            escapeHtml(t('filters.clear_all', 'Clear all')) +
            '</span>' +
            '</span>' +
            '</a>' +
          '</facet-remove>'
        );
      });
    });
    var price = priceFromSearch(window.location.search);
    if (price.min || price.max) {
      var qsPrice = removePriceParams(window.location.search);
      var priceHref = window.location.pathname + (qsPrice ? '?' + qsPrice : '');
      var priceLabel = (price.min || '0') + ' – ' + (price.max || '…');
      chips.push(
        '<facet-remove>' +
          '<a href="' +
          escapeHtml(priceHref) +
          '" class="active-facets__button active-facets__button--light js-facet-remove">' +
          '<span class="active-facets__button-inner button button--tertiary">' +
          escapeHtml(priceLabel) +
          ' ×' +
          '</span>' +
          '</a>' +
        '</facet-remove>'
      );
    }
    list.innerHTML = chips.join('');
    wrap.classList.toggle('has-active-filters', chips.length > 0);
  }

  function syncFromLocation() {
    var nextApplied = appliedFromSearch(window.location.search);
    applied = nextApplied;
    if (!drawer || !drawer.classList.contains('active')) {
      pending = clonePending(applied);
    }
    seedHiddenInputs(applied);
    updateChipCounts(applied);
    renderActiveChips(applied);
    syncUnavailableChips();
  }

  function onView() {
    commitSort();
    var params = buildSearchParams(pending, extrasFromLocation(window.location.search, sortValue()));
    applying = true;
    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      FacetFiltersForm.renderPage(params);
    } else {
      window.location.search = params ? '?' + params : '';
    }
    if (drawer && typeof drawer.close === 'function') drawer.close();
    applying = false;
  }

  function openFromButton(btn) {
    var data = getData();
    if (!data) return;
    applied = appliedFromSearch(window.location.search);
    pending = clonePending(applied);
    appliedSort = liveSortValue();
    pendingSort = appliedSort;
    ensureDrawer();
    setExpanded(btn);
    var input = queryInput();
    if (input) input.value = '';
    updateSearchClear();
    var param = btn.getAttribute('data-param');
    if (param) {
      renderIndex();
      showFacet(param);
    } else {
      renderIndex();
      showIndex();
    }
    updateFooter();
    drawer.open(btn);
    ensureValues().then(function () {
      if (!drawer || !drawer.classList.contains('active')) return;
      if (param && mode === 'nested' && currentParam === param) {
        var loaded = getData();
        var facet = findFacet(loaded && loaded.facets, param);
        if (facet) renderOptions(facet);
        else showIndex();
      } else if (mode === 'all') {
        renderIndex();
      }
      updateFooter();
    });
  }

  function enhanceEmptyState() {
    var root = document.querySelector('[data-fab-filter-empty]');
    if (!root) return;
    var appliedState = appliedFromSearch(window.location.search);
    var pairs = [];
    Object.keys(appliedState).forEach(function (name) {
      Object.keys(appliedState[name] || {}).forEach(function (value) {
        if (appliedState[name][value]) pairs.push({ name: name, value: value });
      });
    });
    var titleEl = root.querySelector('[data-fab-filter-empty-title]');
    var actionsEl = root.querySelector('[data-fab-filter-empty-actions]');
    var clearUrl = root.getAttribute('data-clear-url') || window.location.pathname;

    if (!pairs.length) {
      if (titleEl) titleEl.textContent = t('filters.empty_title', 'No prints in this collection.').replace('{list}', '');
      if (actionsEl) {
        actionsEl.innerHTML =
          '<li><a class="fab-filter-empty__action" href="' +
          escapeHtml(clearUrl) +
          '">' +
          escapeHtml(t('filters.see_all', 'See all prints')) +
          '</a></li>';
      }
      return;
    }

    var labels = pairs.map(function (pair) {
      return labelForValue((getData() && getData().facets) || [], pair.name, pair.value);
    });
    if (titleEl) {
      titleEl.textContent = t('filters.empty_title', 'No {list} prints in this collection.').replace(
        '{list}',
        labels.join(' ')
      );
    }
    if (!actionsEl) return;
    actionsEl.innerHTML = pairs
      .map(function (pair) {
        var remainingPairs = pairs.filter(function (other) {
          return other.name !== pair.name || other.value !== pair.value;
        });
        var remaining = remainingPairs.map(function (other) {
          return labelForValue((getData() && getData().facets) || [], other.name, other.value);
        });
        var href = window.location.pathname;
        var qs = removeSearchParamValue(window.location.search, pair.name, pair.value);
        if (qs) href += '?' + qs;
        var actionLabel =
          remaining.length === 0
            ? t('filters.see_all', 'See all prints')
            : t('filters.see_prints', 'See {list} prints').replace('{list}', remaining.join(' '));
        return (
          '<li><a class="fab-filter-empty__action" href="' +
          escapeHtml(href) +
          '" data-relax-url="' +
          escapeHtml(href) +
          '" data-relax-labels="' +
          escapeHtml(remaining.join(' ')) +
          '">' +
          escapeHtml(actionLabel) +
          '</a></li>'
        );
      })
      .join('');

    var grid = document.getElementById('ProductGridContainer');
    var sectionId = grid && (grid.dataset.sectionId || (grid.closest('[data-section-id]') || {}).dataset.sectionId);
    if (!sectionId || !window.fetch) return;
    actionsEl.querySelectorAll('[data-relax-url]').forEach(function (link) {
      var relaxUrl = link.getAttribute('data-relax-url');
      if (!relaxUrl) return;
      var join = relaxUrl.indexOf('?') === -1 ? '?' : '&';
      fetch(relaxUrl + join + 'section_id=' + encodeURIComponent(sectionId))
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var count = parseCountFromHtml(html);
          if (count == null || isNaN(count)) return;
          var remainingLabels = link.getAttribute('data-relax-labels') || '';
          link.textContent = t('filters.see_count', 'See {n} {list} prints')
            .replace('{n}', String(count))
            .replace('{list}', remainingLabels);
        })
        .catch(function () {});
    });
  }

  function bind() {
    if (bound) return;
    bound = true;
    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-fab-filter-open]');
      if (!btn) return;
      event.preventDefault();
      openFromButton(btn);
    });
    document.addEventListener(
      'pointerover',
      function (event) {
        var btn = event.target.closest && event.target.closest('[data-fab-filter-open]');
        if (!btn || !document.querySelector('.fab-filters')) return;
        ensureValues();
      },
      true
    );
    document.addEventListener(
      'pointerdown',
      function (event) {
        var btn = event.target.closest && event.target.closest('[data-fab-filter-open]');
        if (!btn || !document.querySelector('.fab-filters')) return;
        ensureValues();
      },
      true
    );
    document.addEventListener('focusin', function (event) {
      var btn = event.target.closest && event.target.closest('[data-fab-filter-open]');
      if (!btn || !document.querySelector('.fab-filters')) return;
      ensureValues();
    });
  }

  function init() {
    bind();
    if (dataEl()) syncFromLocation();
    var emptyRoot = document.querySelector('[data-fab-filter-empty]');
    if (emptyRoot) enhanceEmptyState();
    var appliedState = appliedFromSearch(window.location.search);
    if (dataEl() && (totalSelected(appliedState) > 0 || emptyRoot)) {
      ensureValues().then(function () {
        syncFromLocation();
        if (document.querySelector('[data-fab-filter-empty]')) enhanceEmptyState();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  helpers.enhanceEmptyState = enhanceEmptyState;
  helpers.syncFromLocation = syncFromLocation;
  helpers.init = init;
  return helpers;
});
